import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { requestBankAccountNo } from "utils/formatter/formatCommon";
import { Flex, message, Modal } from "antd";
import { generateRandomString } from "utils/helper";
import * as services from "../services/employeeApi";
import * as service from "../services/salaryApi";
import useOptions from "features/masterData/hooks/useOptions";
import * as slices from "../slices/employeeSlice";
import _ from "lodash";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const getChangedFields = (newValues, oldValues) =>
  Object.keys(newValues).reduce((acc, key) => {
    if (!_.isEqual(newValues[key], oldValues[key])) {
      acc[key] = newValues[key];
    }
    return acc;
  }, {});

const useFetchOptions = (serviceFunction, companyId, groupType, dispatchFunction) => {
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchData = async () => {
      dispatch(slices.setIsLoading(true));
      try {
        const response = await serviceFunction({
          requestId: generateRandomString(),
          companyId,
          groupType,
        });
        dispatch(dispatchFunction(response?.data));
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        dispatch(slices.setIsLoading(false));
      }
    };

    if (companyId) {
      fetchData();
    }
  }, [companyId, serviceFunction, groupType, dispatchFunction, dispatch]);
};

function useEmployeeEmploymentInfo(
  nav = true
) {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams();

  const { movementGroupOptions, getMovementGroupOptions } = useOptions();
  const { employeeTypeOptions, getEmployeeTypeOptions } = useOptions();
  const { employeeStatusTypeOptions, getEmployeeStstuaTypeOptions } = useOptions();
  const { employmentTypeOptions, getEmploymentTypeOptions } = useOptions();
  const { reasonForSeparationOptions, getReasonForSeparationOptions } = useOptions();
  const [isVisible, setIsVisible] = useState(false)

  dayjs.locale(i18n.language === "th" ? "th" : "en");

  const formatDate = (date) => {
    const formattedDate = dayjs(date).format(i18n.language === "th" ? "DD/MM/BBBB" : "DD/MM/YYYY");
    return formattedDate;
  };

  const handleToggleVisibility = () => {
    setIsVisible(prev => {
      const updatedVisible = !prev;
      getEmploymentInformation(updatedVisible, false);
      return updatedVisible;
    });
  };

  const {
    bankOptions,
    isLoading,
    currentCompany,
    currentEmployee,
    currentEmployment,
    employeeOptions,
    departmentOptions,
    positionOptions,
    shiftOptions,
    currentWorking,
    branchOptions,
    // workInLocationOptions,
    holidayCalendarOptions,
  } = useSelector((state) => ({
    bankOptions: state.employee.bankOptions,
    isLoading: state.employee.isLoading,
    currentCompany: state.company.currentCompany,
    currentEmployee: state.company.currentEmployee,
    currentEmployment: state.employee.currentEmployment,
    employeeOptions: state.employee.employeeOptions,
    departmentOptions: state.employee.departmentOptions,
    positionOptions: state.employee.positionOptions,
    shiftOptions: state.employee.shiftOptions,
    currentWorking: state.employee.currentWorking,
    branchOptions: state.employee.branchOptions,
    // workInLocationOptions: state.employee.workInLocationOptions,
    holidayCalendarOptions: state.employee.holidayCalendarOptions,
  }));

  const toInitialValues = (initialValues = {}) => {
    // console.log("initialValues", initialValues, (currentEmployment?.extensionProbationEx != 'Y' &&
    //   currentEmployment?.extensionProbation != 'Y'));
    // console.log("initialValues", (currentEmployment?.extensionProbationEx != 'Y' &&
    //   currentEmployment?.extensionProbation != 'Y') ? currentEmployment?.probationDate ? dayjs(currentEmployment?.probationDate, 'YYYY-MM-DD') : "-" : currentEmployment?.employmentDate
    //   ? dayjs(currentEmployment?.employmentDate, 'YYYY-MM-DD').add(
    //     (currentEmployment?.extensionProbationEx === 'Y' ? parseInt(currentEmployment?.daysProbationEx ?? 0) : 0) +
    //     (currentEmployment?.extensionProbation === 'Y' ? parseInt(currentEmployment?.daysProbation ?? 0) : 0),
    //     'days') : null);

    return initialValues;
  };


  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isModal, setModal] = useState(false);
  const [isMovement, setMovement] = useState({});
  const [isProbation, setProbation] = useState({});

  useFetchOptions(service.getBankOption, currentCompany, "", slices.setBankOptions);
  useFetchOptions(services.getDropDown, currentCompany, "", slices.setEmployeeOptions);
  useFetchOptions(services.getDepartmentOptions, currentCompany, "", slices.setDepartmentOptions);
  useFetchOptions(services.getPositionOptions, currentCompany, "", slices.setPositionOptions);
  useFetchOptions(services.getShiftOptions, currentCompany, "", slices.setShiftOptions);
  useFetchOptions(services.getBranch, currentCompany, "", slices.setBranchOptions);
  // useFetchOptions(services.getWorkInLocationOptions, currentCompany, "", slices.setWorkInLocationOptions);
  useFetchOptions(services.getHolidayCalendarOptions, currentCompany, "", slices.setHolidayCalendarOptions);

  useEffect(() => {
    if (currentCompany) {
      getEmployeeTypeOptions({ companyId: currentCompany, groupType: "EMPLOYEE_TYPE" });
      getEmployeeStstuaTypeOptions();
      getEmploymentTypeOptions({ companyId: currentCompany });
      getReasonForSeparationOptions({ companyId: currentCompany, groupType: "REASON_FOR_SEPARATION" });
    }
  }, [currentCompany, getEmployeeTypeOptions, getEmployeeStstuaTypeOptions, getEmploymentTypeOptions, getReasonForSeparationOptions]);

  const modifiedOptions = (options) =>
    options?.map((opt) => ({
      ...opt,
      label: <Flex>
        {opt?.icon && (
          <img
            src={opt?.icon}
            alt="Bank icon"
            style={{ width: "2.3vw", height: "3.8vh", marginRight: "5px" }} />
        )}
        {i18n.language === "en" ? (opt?.nameEn ? opt?.nameEn : opt.name) : opt.name}</Flex>,
      value: opt.id,
    }));

  const modifiedBankOptions = useMemo(() => modifiedOptions(bankOptions), [bankOptions, i18n.language]);
  const modifiedEmployeeOptions = useMemo(() => {

    return employeeOptions?.filter(option => option?.id !== id).map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.fullNameEn : opt.fullName,
      value: opt.id,
    }));

  }, [employeeOptions, id, i18n.language]);
  // const modifiedEmployeeOptions = useMemo(() => {
  //   return modifiedOptions(employeeOptions?.filter(option => option?.id !== id));
  // }, [employeeOptions, id, i18n.language]);
  const modifiedDepartmentOptions = useMemo(() => modifiedOptions(departmentOptions), [departmentOptions, i18n.language]);
  const modifiedPositionOptions = useMemo(() => modifiedOptions(positionOptions), [positionOptions, i18n.language]);
  const modifiedShiftOptions = useMemo(() => modifiedOptions(shiftOptions), [shiftOptions, i18n.language]);
  const modifiedBranchOptions = useMemo(() => modifiedOptions(branchOptions), [branchOptions, i18n.language]);
  // const modifiedWorkInLocationOptions = useMemo(() => modifiedOptions(workInLocationOptions), [workInLocationOptions, i18n.language]);
  const modifiedHolidayCalendarOptions = useMemo(() => modifiedOptions(holidayCalendarOptions), [holidayCalendarOptions, i18n.language]);
  // console.log('currentEmployment', parseInt(currentWorking?.dayOfProbation));
  const initialValues = useMemo(() => toInitialValues(
    {
      ...currentEmployment,
      reasonOfSeparation: currentEmployment?.reasonOfSeparation ? currentEmployment?.reasonOfSeparation?.id : null,
      dateContractExpire: currentEmployment?.dateContractExpire ? dayjs(currentEmployment?.dateContractExpire, 'YYYY-MM-DD') : null,
      driverExpire: currentEmployment?.driverExpire ? dayjs(currentEmployment?.driverExpire, 'YYYY-MM-DD') : null,
      employmentDate: currentEmployment?.employmentDate ? dayjs(currentEmployment?.employmentDate, 'YYYY-MM-DD') : null,
      dateExpiration: currentEmployment?.dateExpiration ? dayjs(currentEmployment?.dateExpiration, 'YYYY-MM-DD') : null,
      dateEffective: currentEmployment?.dateEffective ? dayjs(currentEmployment?.dateEffective, 'YYYY-MM-DD') : null,
      probationDate: (currentEmployment?.extensionProbationEx != 'Y' &&
        currentEmployment?.extensionProbation != 'Y') ?
        dayjs(currentEmployment?.employmentDate, 'YYYY-MM-DD').add(currentWorking?.dayOfProbation - 1, 'days') : currentEmployment?.employmentDate
          ? dayjs(currentEmployment?.employmentDate, 'YYYY-MM-DD').add(
            (currentEmployment?.extensionProbationEx === 'Y' ? parseInt(currentEmployment?.daysProbationEx ?? 0) : 0) +
            (currentEmployment?.extensionProbation === 'Y' ? parseInt(currentEmployment?.daysProbation ?? 0) : 0) - 1,
            'days'
          )
          : null,
      branchId: currentEmployment?.branch ? currentEmployment?.branch?.id : null,
    }
  ), [currentEmployment]);

  // console.log('currentEmployment', currentEmployment?.extensionProbation === 'Y');
  // console.log('currentEmployment', currentEmployment?.daysProbation);
  // console.log('currentEmployment', dayjs(currentEmployment?.employmentDate, 'YYYY-MM-DD').add(parseInt(currentWorking?.dayOfProbation) - 1 + parseInt(currentEmployment?.daysProbation), 'days'));

  const onEdit = useCallback(() => {
    handleVisableAndEdit(true)
    getEmploymentInformation(true, true)
  }, []);

  const handleVisableAndEdit = (flag) => {
    setIsEditing(flag)
    setIsVisible(flag)
  }

  const onCancel = useCallback(() => {
    handleVisableAndEdit(false)
    getEmploymentInformation()
  }, []);


  const getCompanyWorking = useCallback(async () => {
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getWorkingCondition({
        requestId: generateRandomString(),
        companyId: currentCompany,
      });
      dispatch(slices.setCurrentWorking(response.data));

    } catch (error) {
      // alert({ type: "error", resultObject: error });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [currentCompany, dispatch]);

  const getEmploymentInformation = useCallback(async (visible = false, edit = false) => {
    dispatch(slices.setIsLoading(true));
    try {
      const response = await services.getEmploymentInformation({
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: id,
        visible: visible,
        isEdit: edit
      });
      const modifiedValues = {
        ...response?.data,
        bankAccountId: response?.data?.bankAccount?.id,
        shiftId: response?.data?.shift?.id,
        employmentTypeId: response?.data?.employmentType?.id,
        employeeStatusId: response?.data?.employeeStatus?.id,
        employeeStatusCode: response?.data?.employeeStatus?.code,
        employeeTypeId: response?.data?.employeeType?.id,
        positionId: response?.data?.position?.id,
        departmentId: response?.data?.department?.id,
        holidayCalendarId: response?.data?.holidayCalendar?.id,
        supervisorId: response?.data?.supervisor.map((supervisor) => supervisor.id),
        workLocationId: response?.data?.workLocation.map((workLocation) => workLocation.id),
        requestId: generateRandomString(),
        reasonOfSeparation: response?.data?.reasonOfSeparation,
        companyId: currentCompany,
        manpowerType: response?.data?.manpowerType ?? 'NEW',
        recordTime: response?.data?.recordTime ?? 'RECORD',
        paymentMethod: response?.data?.paymentMethod,
        bankAccountNo: response?.data?.bankAccountNo,
        companyName: i18n.language === "en" ? response?.data?.company?.nameEn : response?.data?.company?.name,
        otRequestUnable: response?.data?.otRequestUnable === "Y" ? "N" : "Y"
      };
      dispatch(slices.setCurrentEmployment(modifiedValues || {}));
    } catch (error) {
      // Alert({ type: "error", resultObject: error });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [currentCompany, dispatch, i18n.language, id, getCompanyWorking]);



  const updateEmploymentInformation = useCallback(async (values) => {
    dispatch(slices.setIsLoading(true));
    const checktype03 = values?.newValue?.employeeStatus?.code !== "EMPLOYEE_STATUS_TYPE_03";
    const checktype02 = values?.newValue?.employeeStatus?.code !== "EMPLOYEE_STATUS_TYPE_02";
    try {

      let supervisorNew = values?.newValue?.supervisorId[0]
        ? employeeOptions?.filter((item) => values?.newValue?.supervisorId?.includes(item?.id))
        : [];

      if (Array.isArray(values?.newValue?.supervisorId) && values.newValue.supervisorId.length > 0) {
        if (typeof values.newValue.supervisorId[0] === 'object') {
          values.newValue.supervisorId = values.newValue.supervisorId.map(item => item.value);
        }
      } else {
        values.newValue.supervisorId = values?.newValue?.supervisorId;
      }
      delete values?.newValue?.employeeImage
      delete values?.oldValue?.employeeImage

      const response = await services.updateEmploymentInformation({
        ...values,
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: id,
        actionId: currentEmployee,
        newValue: {
          ...values?.newValue,
          effectiveDate: values?.newValue?.effectiveDate ? dayjs(values?.newValue?.effectiveDate).format("YYYY-MM-DD") : null,
          dateExpiration: checktype03 ? null : values?.newValue?.dateExpiration ? dayjs(values?.newValue?.dateExpiration).format("YYYY-MM-DD") : null,
          dateEffective: checktype02 ? null : values?.newValue?.dateEffective ? dayjs(values?.newValue?.dateEffective).format("YYYY-MM-DD") : null,
          driverExpire: values?.newValue?.driverExpire ? dayjs(values?.newValue?.driverExpire).format("YYYY-MM-DD") : null,
          reasonOfSeparation: checktype03 ? null : values?.newValue?.reasonOfSeparation,
          reasonForSeparation: checktype03 ? null : values?.newValue?.reasonForSeparation,
          dateContractExpire: values?.newValue?.dateContractExpire ? dayjs(values?.newValue?.dateContractExpire).format("YYYY-MM-DD") : null,
          driverExpire: values?.newValue?.driverExpire ? dayjs(values?.newValue?.driverExpire).format("YYYY-MM-DD") : null,
          probationDate: values?.newValue?.probationDate ? dayjs(values?.newValue?.probationDate).format("YYYY-MM-DD") : null,
          supervisor: supervisorNew,
          otRequestUnable: values?.newValue?.otRequestUnable === "Y" ? "N" : "Y",
        }
      });

      if (response?.data?.status?.code === "200") {
        message.success(t("common.success"))
        setIsEditing(false);
        getEmploymentInformation();
        return;
      }

      if (response?.data?.status?.code === "400") {
        Modal.error({
          content: `${response?.data?.status?.details?.[0]?.key} ${response?.data?.status?.details?.[0]?.value}`,
          okText: t("message.ok"),
        });
        return;
      }

    } catch (error) {
      Modal.error({
        content: `${error?.data?.status?.details?.[0]?.key} ${error?.data?.status?.details?.[0]?.value}`,
        okText: t("message.ok"),
      });
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [currentCompany, currentEmployee, dispatch, getEmploymentInformation, id]);


  const updateEmploymentInformationWithMovement = useCallback(async (values) => {
    dispatch(slices.setIsLoading(true));
    const checktype03 = values?.oldValue?.employeeStatus?.code !== "EMPLOYEE_STATUS_TYPE_03";
    const checktype02 = values?.oldValue?.employeeStatus?.code !== "EMPLOYEE_STATUS_TYPE_02";

    // console.log(values?.newValue)
    // const checktype03 = values?.employeeStatus?.code !== "EMPLOYEE_STATUS_TYPE_03";
    try {
      delete values?.newValue?.employeeImage
      delete values?.oldValue?.employeeImage
      await services.updateEmploymentInformationWithMovement({
        ...values,
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: id,
        actionId: currentEmployee,
        // email: null,
        newValue: {
          ...values.oldValue,
          effectiveDate: values?.oldValue?.effectiveDate ? dayjs(values?.oldValue?.effectiveDate).format("YYYY-MM-DD") : null,
          dateExpiration: checktype03 ? null : values?.oldValue?.dateExpiration ? dayjs(values?.oldValue?.dateExpiration).format("YYYY-MM-DD") : null,
          dateEffective: checktype02 ? null : values?.oldValue?.dateEffective ? dayjs(values?.oldValue?.dateEffective).format("YYYY-MM-DD") : null,
          reasonOfSeparation: checktype03 ? null : values?.oldValue?.reasonOfSeparation,
          reasonForSeparation: checktype03 ? null : values?.oldValue?.reasonForSeparation,
          dateContractExpire: values?.oldValue?.dateContractExpire ? dayjs(values?.oldValue?.dateContractExpire).format("YYYY-MM-DD") : null,
          driverExpire: values?.oldValue?.driverExpire ? dayjs(values?.oldValue?.driverExpire).format("YYYY-MM-DD") : null,
          probationDate: values?.oldValue?.probationDate ? dayjs(values?.oldValue?.probationDate).format("YYYY-MM-DD") : null,
          supervisor: values?.oldValue?.supervisorId?.map((item, index) => ({
            seq: index + 1,
            employeeId: item.value ?? item,
          })),
          supervisorId: values?.oldValue?.supervisorId?.map((item, index) =>
            item.value ?? item
          ),
          paymentMethod: values?.newValue?.paymentMethod,
          promptpayNo: values?.newValue?.promptpayNo,
          extensionProbationEx: values?.newValue?.extensionProbationEx,
          daysProbationEx: values?.newValue?.daysProbationEx,
          holidayCalendarId: values?.newValue?.holidayCalendar?.id,
          driverNo: values?.newValue?.driverNo,
          driverExpire: values?.newValue?.driverExpire ? dayjs(values?.newValue?.driverExpire).format("YYYY-MM-DD") : null,
          branch: values?.newValue?.branch,
          branchId: values?.newValue?.branch?.id,
          shift: values?.newValue?.shift,
          shiftId: values?.newValue?.shift?.id,
          employeeNo: values?.newValue?.employeeNo,
          employeeStatus: values?.newValue?.employeeStatus,
          employeeStatusCode: values?.oldValue?.employeeStatusCode,
          employeeType: values?.oldValue?.employeeType,
          bankAccountNo: values?.newValue?.bankAccountNo,
          bankAccount: values?.newValue?.bankAccount,
          holidayCalendar: values?.newValue?.holidayCalendar,
          otRequestUnable: values?.newValue?.otRequestUnable
        }
        // salary: values?.newValue?.salary
      });
      message.success(t("common.success"))
      setIsEditing(false);
      getEmploymentInformation();
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [currentCompany, currentEmployee, dispatch, getEmploymentInformation, id]);

  const onSubmit = useCallback((values) => {
    console.log("values", values);
    Modal.confirm({
      title: t('generalInfo.confirmation'),
      content: (
        <>
          <p>{t('generaInfo.are-you-sure-confirm-to-edit')}</p>
        </>
      ),
      onOk() {
        values = {
          ...values,
          dateContractExpire: values?.dateContractExpire ? dayjs(values?.dateContractExpire).format("YYYY-MM-DD") : null,
          employmentDate: dayjs(values?.employmentDate).format('YYYY-MM-DD'),
          bankAccountNo: values?.bankAccountNo ? requestBankAccountNo(values?.bankAccountNo) : ""
        }

        if (_.isEqual(values, currentEmployment)) {
          updateEmploymentInformation(values);
        } else {
          // updateEmploymentInformationWithMovement(values);
          const changedOldFields = getChangedFields(currentEmployment, values);
          const changedNewFields = getChangedFields(values, currentEmployment);
          const movementList = [];
          console.log("changedOldFields", changedOldFields, changedNewFields,);

          let employeeStatusOld = employeeStatusTypeOptions.find((item) => item?.id === currentEmployment?.employeeStatusId);
          let employeeStatusNew = employeeStatusTypeOptions.find((item) => item?.id === values?.employeeStatusId);

          // console.log('values', values?.employeeStatusNew)

          // ! PERMANENT_EMPLOYMENT

          if (!_.isEqual(values?.employeeTypeId, currentEmployment?.employeeTypeId)) {
            const movementGroup = movementGroupOptions?.filter((option) => option?.value === "MOVEMENT_01_06");
            movementList.push(movementGroup[0]?.value);
          }

          if (
            // (employeeStatusNew?.code === "EMPLOYEE_STATUS_TYPE_02" && employeeStatusOld?.code === "EMPLOYEE_STATUS_TYPE_02") ||
            (!_.isEqual(dayjs(initialValues?.employmentDate).format("YYYY-MM-DD"), dayjs(values?.employmentDate).format("YYYY-MM-DD"),))

          ) {
            const movementGroup = movementGroupOptions?.filter((option) => option?.value === "MOVEMENT_01_02");
            movementList.push(movementGroup[0]?.value);
          }
          // ! SEPARATION
          // console.log('values', values?.dateExpiration)
          // console.log('currentEmployment', currentEmployment?.dateExpiration)
          // console.log('values', values?.reasonOfSeparation)
          // console.log('currentEmployment', currentEmployment?.reasonOfSeparation?.id)
          if (
            (employeeStatusNew?.code === "EMPLOYEE_STATUS_TYPE_03" && employeeStatusOld?.code !== "EMPLOYEE_STATUS_TYPE_03") || (dayjs(values?.dateExpiration).format("YYYY-MM-DD") !== dayjs(currentEmployment?.dateExpiration).format("YYYY-MM-DD") || values?.reasonOfSeparation !== currentEmployment?.reasonOfSeparation?.id)
          ) {
            const movementGroup = movementGroupOptions?.filter((option) => option?.value === "MOVEMENT_01_03");
            movementList.push(movementGroup[0]?.value);
          }
          // console.log('values', values?.extensionProbation)
          // console.log('values', dayjs(values?.probationDate).format("YYYY-MM-DD"))
          // console.log('values', currentEmployment?.probationDate)
          // ! Renew Probation
          if (
            employeeStatusNew?.code === "EMPLOYEE_STATUS_TYPE_01" && (values?.extensionProbation === "Y" && dayjs(values?.probationDate).format("YYYY-MM-DD") !== currentEmployment?.probationDate)
          ) {
            const movementGroup = movementGroupOptions?.filter((option) => option?.value === "MOVEMENT_01_05");
            movementList.push(movementGroup[0]?.value);
          }

          // let supervisorOld = Array.isArray(currentEmployment?.supervisorId)
          //   ? employeeOptions?.filter((item) => currentEmployment?.supervisorId?.includes(item?.id))
          //   : [];

          let supervisorOld = Array.isArray(currentEmployment?.supervisorId) && currentEmployment?.supervisorId.length > 0 && Array.isArray(employeeOptions)
            ? employeeOptions.filter((item) => {
              const firstItem = currentEmployment?.supervisorId[0];
              if (typeof firstItem === 'string') {
                return currentEmployment?.supervisorId.includes(item.id);
              } else if (typeof firstItem === 'object') {
                return currentEmployment?.supervisorId.some(sup => sup.value === item.id);
              }
              return false;
            })
            : [];


          // let supervisorNew = supervisorOld


          // console.log('values', supervisorNew)

          let supervisorNew = Array.isArray(values?.supervisorId) && values.supervisorId.length > 0 && Array.isArray(employeeOptions)
            ? employeeOptions.filter((item) => {
              const firstItem = values.supervisorId[0];
              if (typeof firstItem === 'string') {
                return values.supervisorId.includes(item.id);
              } else if (typeof firstItem === 'object') {
                return values.supervisorId.some(sup => sup.value === item.id);
              }
              return false;
            })
            : [];
          let positionOld = positionOptions.find((item) => item?.id === currentEmployment?.positionId);
          let positionNew = positionOptions.find((item) => item?.id === values?.positionId);

          // ! SUPERVISOR_REASSIGNMENT
          if (
            !_.isEqual(supervisorOld, supervisorNew)
            // && _.isEqual(positionOld, positionNew)
          ) {
            const movementGroup = movementGroupOptions?.filter((option) => option?.value === "MOVEMENT_02_03");
            movementList.push(movementGroup[0]?.value);
          }


          // if ((values?.employmentDate !== currentEmployment?.employmentDate)) {
          //   const movementGroup = movementGroupOptions?.filter((option) => option?.value === "MOVEMENT_01_01");
          //   movementList.push(movementGroup[0]?.value);
          // }
          // ! EMPLOYMENT_CONDITION_CHANGE
          if (
            // !_.isEqual(employeeStatusOld, employeeStatusNew) && movementList.length < 1
            ((!_.isEqual(employeeStatusOld, employeeStatusNew) && employeeStatusNew?.code !== "EMPLOYEE_STATUS_TYPE_03"))
          ) {
            const movementGroup = movementGroupOptions?.filter((option) => option?.value === "MOVEMENT_01_01");
            movementList.push(movementGroup[0]?.value);
          }


          let employmentTypeOld = employmentTypeOptions.find((item) => item?.id === currentEmployment?.employmentTypeId);
          let employmentTypeNew = employmentTypeOptions.find((item) => item?.id === values?.employmentTypeId);

          if (
            !_.isEqual(employmentTypeOld, employmentTypeNew)
          ) {
            const movementGroup = movementGroupOptions?.filter((option) => option?.value === "MOVEMENT_03_02");
            movementList.push(movementGroup[0]?.value);
          }
          // ! REHIRE
          // if (
          //   employeeStatusNew.code === "EMPLOYEE_STATUS_TYPE_03"
          // ) {
          //   const movementGroup = movementGroupOptions?.filter((option) => option?.value === "MOVEMENT_01_04");
          //   movementList.push(movementGroup[0].value);
          // }

          // ! PROMOTION
          if (
            !_.isEqual(positionOld, positionNew)
          ) {
            const movementGroup = movementGroupOptions?.filter((option) => option?.value === "MOVEMENT_02_01");
            movementList.push(movementGroup[0]?.value);
          }

          let departmentOld = departmentOptions.find((item) => item.id === currentEmployment?.departmentId);
          let departmentNew = departmentOptions.find((item) => item.id === values?.departmentId);
          // ! DEPARTMENT_TRANSFER
          if (
            !_.isEqual(departmentOld, departmentNew)
          ) {
            const movementGroup = movementGroupOptions?.filter((option) => option?.value === "MOVEMENT_02_02");
            movementList.push(movementGroup[0].value);
          }

          // ?
          let employeeTypeOld = employeeTypeOptions.find((item) => item?.id === currentEmployment?.employeeTypeId);
          let employeeTypeNew = employeeTypeOptions.find((item) => item?.id === values?.employeeTypeId);

          let holidayCalendarOld = holidayCalendarOptions?.find((item) => item?.id === currentEmployment?.holidayCalendarId);
          let holidayCalendarNew = holidayCalendarOptions?.find((item) => item?.id === values?.holidayCalendarId);
          let workLocationOld = branchOptions?.find((item) => item?.id === currentEmployment?.workLocationId);
          let workLocationNew = branchOptions?.find((item) => item?.id === values?.workLocationId);
          let shiftOld = shiftOptions?.find((item) => item?.id === currentEmployment?.shiftId);
          let shiftNew = shiftOptions?.find((item) => item?.id === values?.shiftId);
          let bankAccountOld = bankOptions?.find((item) => item?.id === currentEmployment?.bankAccountId);
          let bankAccountNew = bankOptions?.find((item) => item?.id === values?.bankAccountId);
          let branchOld = branchOptions?.find((item) => item?.id === currentEmployment?.branch?.id);
          let branchNew = branchOptions?.find((item) => item?.id === values?.branchId);


          let dateExpirationOld = dayjs(currentEmployment?.dateExpiration).format("YYYY-MM-DD");
          let dateEffectiveOld = dayjs(currentEmployment?.dateEffective).format("YYYY-MM-DD");
          let dateExpirationNew = dayjs(values?.dateExpiration).format("YYYY-MM-DD");
          let dateEffectiveNew = dayjs(values?.dateEffective).format("YYYY-MM-DD");
          let reasonForSeparationOld = reasonForSeparationOptions?.find((item) => item?.id === currentEmployment?.reasonOfSeparation?.id);
          let reasonForSeparationNew = reasonForSeparationOptions?.find((item) => item?.id === values?.reasonOfSeparation);

          // console.log('values', values?.driverNo)
          // console.log('values', values)
          // console.log('values', reasonForSeparationOld ?? "-")
          // console.log('values', reasonForSeparationNew ?? "-")
          // console.log('values', values?.reasonOfSeparation)
          // console.log(supervisorOld[0]?.name)
          // console.log('values', employeeStatusOld)
          // console.log('values', workLocationOld)
          // console.log('values', branchOptions?.find((item) => item?.id === initialValues?.branch?.id))
          setMovement({
            movementTypeList: movementList,
            oldValue: {
              ...initialValues,
              probationDate: initialValues?.probationDate ? dayjs(initialValues?.probationDate).format("YYYY-MM-DD") === "Invalid Date" ? null : dayjs(initialValues?.probationDate).format("YYYY-MM-DD") : null,
              dateContractExpire: initialValues?.dateContractExpire ? dayjs(initialValues?.dateContractExpire).format("YYYY-MM-DD") === "Invalid Date" ? null : dayjs(initialValues?.dateContractExpire).format("YYYY-MM-DD") : null,
              employmentDate: initialValues?.employmentDate ? dayjs(initialValues?.employmentDate).format("YYYY-MM-DD") === "Invalid Date" ? null : dayjs(initialValues?.employmentDate).format("YYYY-MM-DD") : null,
              employeeStatus: employeeStatusOld,
              employeeType: employeeTypeOld,
              supervisor: supervisorOld,
              holidayCalendar: holidayCalendarOld,
              department: departmentOld,
              position: positionOld,
              workLocation: workLocationOld,
              shift: shiftOld,
              bankAccount: bankAccountOld,
              employmentType: employmentTypeOld,
              daysProbation: currentEmployment?.daysProbation === "0" ? null : currentEmployment?.daysProbation,
              dateExpiration: dateExpirationOld === "Invalid Date" ? null : dateExpirationOld,
              dateEffective: dateEffectiveOld === "Invalid Date" ? null : dateEffectiveOld,
              reasonForSeparation: reasonForSeparationOld,
              promptpayNo: currentEmployment?.promptpayNo,
              branch: branchOld
            },
            newValue: {
              ...values,
              probationDate: values?.probationDate ? dayjs(values?.probationDate).format("YYYY-MM-DD") : null,
              dateContractExpire: values?.dateContractExpire ? dayjs(values?.dateContractExpire).format("YYYY-MM-DD") : null,
              employmentDate: values?.employmentDate ? dayjs(values?.employmentDate).format("YYYY-MM-DD") : null,
              employeeStatus: employeeStatusNew,
              employeeType: employeeTypeNew,
              supervisor: supervisorNew,
              holidayCalendar: holidayCalendarNew,
              department: departmentNew,
              position: positionNew,
              workLocation: workLocationNew,
              shift: shiftNew,
              bankAccount: bankAccountNew,
              employmentType: employmentTypeNew,
              dateEffective: dateEffectiveNew === "Invalid Date" ? null : dateEffectiveNew,
              reasonForSeparation: reasonForSeparationNew,
              promptpayNo: values?.promptpayNo,
              branch: branchNew
            },
            oldDataShow: {
              ...changedOldFields,
              probationDate: !_.isEqual(values?.probationDate, currentEmployment?.probationDate)
                ? (changedOldFields?.probationDate ? formatDate(changedOldFields?.probationDate) : "-")
                : "-",
              dateContractExpire: !_.isEqual(values?.dateContractExpire, currentEmployment?.dateContractExpire)
                ? (changedOldFields?.dateContractExpire ? formatDate(changedOldFields?.dateContractExpire) : "-")
                : "-",
              employmentDate: !_.isEqual(values?.employmentDate, currentEmployment?.employmentDate)
                ? (changedOldFields?.employmentDate ? formatDate(changedOldFields?.employmentDate) : "-")
                : "-",
              employeeStatus: employeeStatusOld ?? "-",
              employeeType: employeeTypeOld ?? "-",
              driverNo: currentEmployment?.driverNo ?? "-",
              daysProbation: values?.extensionProbation === "Y" && employeeStatusNew?.nameEn === "Probation" ? currentEmployment?.daysProbation ?? "-" : null,
              supervisor: (supervisorOld == null || (Array.isArray(supervisorOld) && supervisorOld.length === 0)) ? "-" : supervisorOld,
              holidayCalendar: holidayCalendarOld ?? "-",
              department: departmentOld ?? "-",
              position: positionOld ?? "-",
              workLocation: workLocationOld ?? "-",
              shift: shiftOld ?? "-",
              bankAccount: bankAccountOld ?? "-",
              employmentType: employmentTypeOld ?? "-",
              dateExpiration: !_.isEqual(values?.dateExpiration, currentEmployment?.dateExpiration)
                ? (changedOldFields?.dateExpiration ? formatDate(changedOldFields?.dateExpiration) : "-")
                : "-",
              reasonForSeparation:
                reasonForSeparationOld ??
                "-",
              branch: branchOld ? branchOld : "-",
              bankAccountNo: currentEmployment?.bankAccountNo ?? "-",
              paymentMethod: currentEmployment?.paymentMethod ?? "-",
              promptpayNo: currentEmployment?.promptpayNo ?? "-",
              otRequestUnable: currentEmployment?.otRequestUnable ?? "-"
            },
            newDataShow: {
              ...changedNewFields,
              probationDate: !_.isEqual(values?.probationDate, currentEmployment?.probationDate)
                ? formatDate(changedNewFields?.probationDate)
                : null,
              dateContractExpire: !_.isEqual(values?.dateContractExpire, currentEmployment?.dateContractExpire)
                ? formatDate(changedNewFields?.dateContractExpire)
                : null,
              employmentDate: !_.isEqual(values?.employmentDate, currentEmployment?.employmentDate)
                ? formatDate(changedNewFields?.employmentDate)
                : null,
              employeeStatus: employeeStatusNew,
              employeeType: employeeTypeNew,
              driverNo: values?.driverNo,
              driverExpire: values?.driverExpire,
              daysProbation: values?.extensionProbation === "Y" ? values?.daysProbation : null,
              supervisor: !_.isEqual(supervisorOld, supervisorNew) ? supervisorNew : null,
              holidayCalendar: holidayCalendarNew,
              department: departmentNew,
              position: positionNew,
              workLocation: workLocationNew,
              shift: shiftNew,
              bankAccount: bankAccountNew,
              employmentType: employmentTypeNew,
              dateExpiration: !_.isEqual(dateExpirationOld, dateExpirationNew)
                ? (formatDate(dateExpirationNew) === "Invalid Date" ? null : formatDate(dateExpirationNew))
                : null,
              reasonForSeparation: reasonForSeparationNew,
              branch: branchNew,
              paymentMethod: values?.paymentMethod,
              bankAccountNo: values?.bankAccountNo,
              promptpayNo: values?.promptpayNo,
              otRequestUnable: values?.otRequestUnable
            },


          });

          setModalOpen(true);

          // resetForm({ values: { ...currentEmployment } });
        }
      },
      okText: t('confirm'),
      cancelText: t('generalInfo.cancel'),
    });
  }, [currentEmployment, updateEmploymentInformation, updateEmploymentInformationWithMovement, isMovement, employeeStatusTypeOptions, employeeTypeOptions, employeeOptions, holidayCalendarOptions, departmentOptions, positionOptions, branchOptions, shiftOptions, bankOptions, employmentTypeOptions, initialValues, movementGroupOptions, reasonForSeparationOptions]);

  const createMovement = useCallback(async (values) => {
    dispatch(slices.setIsLoading(true));
    try {
      await service.createMovement({
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: id,
        actionId: currentEmployee,
        sendingType: "SUBMIT",
        ...values,
        effectiveDate: dayjs(values?.effectiveDate).format("YYYY-MM-DD"),
      });
      message.success(t("common.success"))
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [currentCompany, id, currentEmployee]);

  const onSubmitModal = useCallback(async (values) => {
    dispatch(slices.setIsLoading(true));
    try {
      const requestSave = { ...values, bankAccountNo: values?.bankAccountNo ? requestBankAccountNo(values?.bankAccountNo) : "" };
      if (values?.forceSave === true) {
        await updateEmploymentInformation(requestSave);
      } else {
        console.log("test values ", { ...values?.newValue });
        await updateEmploymentInformationWithMovement(requestSave);
        await createMovement(requestSave);
      }
      setModalOpen(false);
      setMovement({});
      setIsEditing(false);
      setIsVisible(false);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [createMovement, dispatch, updateEmploymentInformation, updateEmploymentInformationWithMovement]);

  useEffect(() => {
    if (currentCompany) {
      getMovementGroupOptions({
        companyId: currentCompany,
        employeeId: id,
      });

      getCompanyWorking();
    }
  }, [currentCompany, getMovementGroupOptions, id, getCompanyWorking]);

  useEffect(() => {
    if (id && currentCompany) {
      getEmploymentInformation();

    }
  }, [currentCompany, id, getEmploymentInformation]);

  const handleCancel = () => {
    onCancel();
    setModalOpen(false);
    setMovement({});
  };

  const handleOpen = (values) => {
    setModal(true);
    setProbation(values);
  };

  const handleClose = () => {
    setModal(false);
    setProbation({});
  };

  const oldDetails = [
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.employeeStatus,
      label: t("salary.label.employee_status"),
      value: isMovement?.oldDataShow?.employeeStatus,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.employeeType,
      label: t("salary.label.employee_type"),
      value: isMovement?.oldDataShow?.employeeType,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.supervisor,
      label: t("employment-information.label.report-to"),
      value: isMovement?.oldDataShow?.supervisor,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.dateContractExpire,
      label: t("salary.label.date_contract_expire"),
      value: isMovement?.oldDataShow?.dateContractExpire,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.holidayCalendar,
      label: t("salary.label.holiday_calendar"),
      value: isMovement?.oldDataShow?.holidayCalendar,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.driverNo,
      label: t("salary.label.driver_no"),
      value: isMovement?.oldDataShow?.driverNo,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.manpowerType,
      label: t("salary.label.manpower_type"),
      value: isMovement?.oldDataShow?.manpowerType,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.recordTime,
      label: t("salary.label.record_time"),
      value: isMovement?.oldDataShow?.recordTime,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.otRequestUnable,
      label: t("employment-information.label.ot_request"),
      value: isMovement?.oldDataShow?.otRequestUnable === "Y" ? t("allow") : isMovement?.oldDataShow?.otRequestUnable === "N" ? t("not-allowed") : "-",
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.probationDate,
      label: t("employee.label.probation_date"),
      value: isMovement?.oldDataShow?.probationDate,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.employmentDate,
      label: t("salary.label.employment_date"),
      value: isMovement?.oldDataShow?.employmentDate,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.employmentType,
      label: t("salary.label.employment_type"),
      value: isMovement?.oldDataShow?.employmentType,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.dateExpiration,
      label: t('employment-information.label.date-of-expriation'),
      value: isMovement?.oldDataShow?.dateExpiration,
    },

    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.extensionProbation,
      label: t("employee.label.extension_probation"),
      value: isMovement?.oldDataShow?.extensionProbation
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.reasonForSeparation,
      label: t("master_data.label.reason_for_separationimport"),
      value: isMovement?.oldDataShow?.reasonForSeparation,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.oldDataShow?.companyName,
      label: t("salary.label.company_name"),
      value: isMovement?.oldDataShow?.companyName,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.oldDataShow?.department,
      label: t("salary.label.department"),
      value: isMovement?.oldDataShow?.department,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.oldDataShow?.position,
      label: t("salary.label.position"),
      value: isMovement?.oldDataShow?.position,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.oldDataShow?.employeeNo,
      label: t("salary.label.employee_no"),
      value: isMovement?.oldDataShow?.employeeNo,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.oldDataShow?.workLocation,
      label: t("salary.label.work_location"),
      value: isMovement?.oldDataShow?.workLocation,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.oldDataShow?.shift,
      label: t("salary.label.shift"),
      value: isMovement?.oldDataShow?.shift,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.oldDataShow?.branch,
      label: t("employee.label.branch"),
      value: isMovement?.oldDataShow?.branch,
    }
    ,
    {
      type: 'Salary',
      open: isMovement?.oldDataShow?.paymentMethod,
      label: t("salary.label.payment_method"),
      value: isMovement?.oldDataShow?.paymentMethod,
    },
    {
      type: 'Salary',
      open: isMovement?.oldDataShow?.promptpayNo,
      label: t("employee.salary.label.promptpayNo"),
      value: isMovement?.oldDataShow?.promptpayNo,
    },
    {
      type: 'Salary',
      open: isMovement?.oldDataShow?.bankAccount,
      label: t("salary.label.bank_account"),
      value: isMovement?.oldDataShow?.bankAccount,
    },
    {
      type: 'Salary',
      open: isMovement?.oldDataShow?.bankAccountNo,
      label: t("salary.label.bank_account_no"),
      value: isMovement?.oldDataShow?.bankAccountNo,
    },

    {
      type: 'Salary',
      open: isMovement?.oldDataShow?.salary,
      label: t("salary.label.salary"),
      value: isMovement?.oldDataShow?.salary,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.oldDataShow?.daysProbation,
      label: t("employee.label.extension_probation"),
      value: isMovement?.oldDataShow?.daysProbation,
    },


  ];

  const newDetails = [
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.employeeStatusId,
      label: t("salary.label.employee_status"),
      value: isMovement?.newDataShow?.employeeStatus,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.employeeTypeId,
      label: t("salary.label.employee_type"),
      value: isMovement?.newDataShow?.employeeType,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.supervisor,
      label: t("employment-information.label.report-to"),
      value: isMovement?.newDataShow?.supervisor,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.dateContractExpire,
      label: t("salary.label.date_contract_expire"),
      value: isMovement?.newDataShow?.dateContractExpire,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.holidayCalendarId,
      label: t("salary.label.holiday_calendar"),
      value: isMovement?.newDataShow?.holidayCalendar,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.driverNo,
      label: t("salary.label.driver_no"),
      value: isMovement?.newDataShow?.driverNo,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.manpowerType,
      label: t("salary.label.manpower_type"),
      value: isMovement?.newDataShow?.manpowerType,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.recordTime,
      label: t("salary.label.record_time"),
      value: isMovement?.newDataShow?.recordTime,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.otRequestUnable,
      label: t("employment-information.label.ot_request"),
      value: isMovement?.newDataShow?.otRequestUnable === "Y"
        ? t("allow")
        : isMovement?.newDataShow?.otRequestUnable === "N"
          ? t("not-allowed")
          : "-",
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.dateExpiration,
      label: t('employment-information.label.date-of-expriation'),
      value: isMovement?.newDataShow?.dateExpiration,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.probationDate,
      label: t("employee.label.probation_date"),
      value: isMovement?.newDataShow?.probationDate,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.employmentDate,
      label: t("salary.label.employment_date"),
      value: isMovement?.newDataShow?.employmentDate,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.employmentTypeId,
      label: t("salary.label.employment_type"),
      value: isMovement?.newDataShow?.employmentType,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.reasonForSeparation,
      label: t("master_data.label.reason_for_separationimport"),
      value: isMovement?.newDataShow?.reasonForSeparation,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.newDataShow?.companyName,
      label: t("salary.label.company_name"),
      value: isMovement?.newDataShow?.companyName?.name,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.newDataShow?.departmentId,
      label: t("salary.label.department"),
      value: isMovement?.newDataShow?.department,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.newDataShow?.positionId,
      label: t("salary.label.position"),
      value: isMovement?.newDataShow?.position,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.newDataShow?.employeeNo,
      label: t("salary.label.employee_no"),
      value: isMovement?.newDataShow?.employeeNo,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.newDataShow?.workLocationId,
      label: t("salary.label.work_location"),
      value: isMovement?.newDataShow?.workLocation,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.newDataShow?.shiftId,
      label: t("salary.label.shift"),
      value: isMovement?.newDataShow?.shift,
    },
    {
      type: 'CorporateInformation',
      open: isMovement?.newDataShow?.branch,
      label: t("employee.label.branch"),
      value: isMovement?.newDataShow?.branch,
    },
    {
      type: 'Salary',
      open: isMovement?.newDataShow?.paymentMethod,
      label: t("salary.label.payment_method"),
      value: isMovement?.newDataShow?.paymentMethod,
    },
    {
      type: 'Salary',
      open: isMovement?.newDataShow?.promptpayNo,
      label: t("employee.salary.label.promptpayNo"),
      value: isMovement?.newDataShow?.promptpayNo,
    },
    {
      type: 'Salary',
      open: isMovement?.newDataShow?.bankAccountId,
      label: t("salary.label.bank_account"),
      value: isMovement?.newDataShow?.bankAccount,
    },
    {
      type: 'Salary',
      open: isMovement?.newDataShow?.bankAccountNo,
      label: t("salary.label.bank_account_no"),
      value: isMovement?.newDataShow?.bankAccountNo,
    },
    {
      type: 'Salary',
      open: isMovement?.newDataShow?.salary,
      label: t("salary.label.salary"),
      value: isMovement?.newDataShow?.salary,
    },
    {
      type: 'EmploymentInformation',
      open: isMovement?.newDataShow?.daysProbation,
      label: t("employee.label.extension_probation"),
      value: isMovement?.newDataShow?.daysProbation
    },
  ];

  return {
    isMovement,
    isModalOpen,
    isLoading,
    isEditing,
    initialValues,
    currentEmployment,
    setModalOpen,
    setMovement,
    onSubmitModal,
    onEdit,
    onCancel,
    onSubmit,
    handleCancel,
    handleOpen,
    currentWorking,
    handleClose,
    isProbation,
    isModal,
    oldDetails,
    newDetails,
    employeeTypeOptions,
    employeeStatusTypeOptions,
    employmentTypeOptions,
    movementGroupOptions,
    bankOptions: modifiedBankOptions,
    employeeOptions: modifiedEmployeeOptions,
    departmentOptions: modifiedDepartmentOptions,
    positionOptions: modifiedPositionOptions,
    branchOptions: modifiedBranchOptions,
    shiftOptions: modifiedShiftOptions,
    // workInLocationOptions: modifiedWorkInLocationOptions,
    holidayCalendarOptions: modifiedHolidayCalendarOptions,
    reasonForSeparationOptions,
    // updateEmploymentInformationWithMovement
    handleToggleVisibility,
    isVisible
  };
}

export default useEmployeeEmploymentInfo;
